import React, { useEffect, useMemo, useState } from "react";
import {
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  AsYouType,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
} from "libphonenumber-js";
import examples from "libphonenumber-js/examples.mobile.json";
import { useAuth0 } from "@auth0/auth0-react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { setEmailVerified, setProfileCompleted } from "../store/common";
import { SERVER_URL } from "../utils/axios";
import {
  isSocialAuth0Sub,
  getAuthenticationMethod,
  isIntuitConnection,
  isEmailVerifiedClaim,
} from "../utils/auth";
import { setPostOnboardingProfileRedirect } from "../utils/postOnboardingRedirect";
import AuthPageShell from "../shared/AuthPageShell";
import BillingWelcomeNotice from "../components/BillingWelcomeNotice";

const inputClass =
  "w-full text-sm rounded-xl px-3 py-2.5 bg-gray-100 border border-gray-200 mt-2 outline-none focus:border-[#007BFF]";

const labelClass = "text-gray-800 font-light text-sm";

const normalizeDigits = (value) => String(value || "").replace(/[^\d]/g, "");

export default function CompleteProfile() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { user: auth0User, isAuthenticated } = useAuth0();
  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const state = query.get("state") || "";
  const sub = query.get("sub") || auth0User?.sub || "";
  const email = query.get("email") || auth0User?.email || "";

  // If user is an unverified Intuit account, redirect immediately to /intuit-unverified
  useEffect(() => {
    const isIntuit = isIntuitConnection(sub);
    const verified = isEmailVerifiedClaim(null, auth0User);
    if (isIntuit && verified === false) {
      navigate("/intuit-unverified", { replace: true });
    }
  }, [sub, auth0User, navigate]);

  const [name, setName] = useState(() => {
    if (auth0User?.name && !auth0User.name.includes("@")) {
      return auth0User.name;
    }
    return "";
  });
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [country, setCountry] = useState("AU");
  const [otp, setOtp] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [otpFeedback, setOtpFeedback] = useState(null);

  /** Auth0 signup callback includes `state`; in-app redirects may only have `sub` + email. */
  const missingAuth0Params = !sub;
  const isAuth0SignupFlow = Boolean(state);
  const isSocialLogin = useMemo(() => isSocialAuth0Sub(sub), [sub]);
  const authMethod = useMemo(() => getAuthenticationMethod({ sub }), [sub]);

  const countryOptions = useMemo(() => {
    const regionNames =
      typeof Intl !== "undefined" && typeof Intl.DisplayNames !== "undefined"
        ? new Intl.DisplayNames(["en"], { type: "region" })
        : null;
    return getCountries()
      .map((c) => {
        const callingCode = `+${getCountryCallingCode(c)}`;
        const countryName = regionNames?.of(c) || c;
        return { value: c, countryName, callingCode };
      })
      .sort((a, b) => a.countryName.localeCompare(b.countryName));
  }, []);

  const phonePlaceholder = useMemo(() => {
    try {
      const ex = getExampleNumber(country, examples);
      const national = ex?.formatNational();
      return national ? `e.g. ${national}` : "e.g. 0412 345 678";
    } catch {
      return "e.g. 0412 345 678";
    }
  }, [country]);

  useEffect(() => {
    if (!name && auth0User?.name && !auth0User.name.includes("@")) {
      setName(auth0User.name);
    }
  }, [auth0User, name]);

  useEffect(() => {
    if (!phone) return;
    const digits = normalizeDigits(phone);
    const reformatted = digits ? new AsYouType(country).input(digits) : phone;
    if (reformatted !== phone) setPhone(reformatted);
  }, [country]);

  const phoneValid = useMemo(() => {
    const v = String(phone || "").trim();
    return v.length > 0 && isValidPhoneNumber(v, country);
  }, [phone, country]);

  const canSendOtp =
    !missingAuth0Params &&
    email &&
    phoneValid &&
    company.trim().length > 0 &&
    !sendingOtp;

  const canVerifyOtp =
    otpSent && otp.replace(/\D/g, "").length === 6 && !verifyingOtp && !otpVerified;

  const emailOtpComplete =
    isSocialLogin || (otpVerified && Boolean(verificationToken));

  const canContinue =
    !missingAuth0Params &&
    phoneValid &&
    company.trim().length > 0 &&
    emailOtpComplete &&
    !loading;

  const handleSendOtp = async () => {
    setError("");
    setOtpFeedback(null);
    if (!canSendOtp || otpVerified) return;
    try {
      setSendingOtp(true);
      const res = await fetch(`${SERVER_URL}/auth/send-email-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email,
          auth0_user_id: sub,
          name: name.trim() || email,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to send verification code");
      }
      setOtpSent(true);
      setOtpFeedback({
        type: "info",
        message: data.message || "Verification code sent to your email.",
      });
    } catch (err) {
      setOtpFeedback({
        type: "error",
        message: err?.message || "Failed to send code",
      });
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError("");
    setOtpFeedback(null);
    if (!canVerifyOtp) return;
    try {
      setVerifyingOtp(true);
      const res = await fetch(`${SERVER_URL}/auth/verify-email-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email,
          auth0_user_id: sub,
          otp: otp.replace(/\D/g, ""),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Invalid verification code");
      }
      setVerificationToken(data.verification_token || "");
      setOtpVerified(true);
      setOtpFeedback(null);
    } catch (err) {
      setOtpFeedback({
        type: "error",
        message: err?.message || "Verification failed",
      });
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!canContinue) return;

    try {
      setLoading(true);
      const parsedPhone = parsePhoneNumberFromString(phone, country);
      if (!parsedPhone?.isValid()) {
        throw new Error("Please enter a valid phone number for the selected country.");
      }

      const payload = {
        auth0_user_id: sub,
        email,
        name: name.trim(),
        phone_number: parsedPhone.number,
        company_number: company.trim(),
        state,
      };
      if (!isSocialLogin) {
        payload.verification_token = verificationToken;
      }

      const res = await fetch(`${SERVER_URL}/auth/complete-profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to save profile");
      }
      dispatch(setEmailVerified(true));
      dispatch(setProfileCompleted(true));
      setPostOnboardingProfileRedirect();
      if (data.continue_url && isAuth0SignupFlow) {
        window.location.href = data.continue_url;
      } else {
        window.location.href = `${window.location.origin}/settings/profile`;
      }
    } catch (err) {
      setError(err?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageShell
      title="Complete your profile"
      subtitle={
        isSocialLogin
          ? "Enter your phone and company name to finish setting up your account."
          : "Enter your details and verify your email with the one-time code we send you."
      }
      showLogout
    >
      <BillingWelcomeNotice className="mb-4" variant="compact" />

      {missingAuth0Params ? (
        <p className="text-red-500 text-sm mb-4">
          {isAuthenticated
            ? "We could not load your account id. Please sign out and sign in again."
            : "This page must be opened from the sign-up flow (missing account id)."}
        </p>
      ) : null}

      {error ? (
        <p className="text-red-500 text-sm mb-3 whitespace-pre-wrap">{error}</p>
      ) : null}

      <form onSubmit={handleSubmit}>
        {email ? (
          <div className="w-full">
            <p className={labelClass}>Email address</p>
            <div className="mt-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5">
              <p className="text-sm font-medium text-gray-800 break-all">{email}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {isSocialLogin
                  ? `Your email was verified when you signed in with ${authMethod.providerName || "your sign-in provider"}.`
                  : "We will send your verification code to this address."}
              </p>
            </div>
          </div>
        ) : null}

        <div className="w-full lg:mt-5 mt-4">
          <p className={labelClass}>
            Name <span className="text-gray-400 text-xs font-normal">(optional)</span>
          </p>
          <input
            type="text"
            className={inputClass}
            placeholder="Your Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={missingAuth0Params}
            autoComplete="name"
          />
        </div>

        <div className="w-full lg:mt-5 mt-4">
          <p className={labelClass}>Phone Number</p>
          <div className="flex gap-2 mt-2">
            <select
              className="text-sm rounded-xl px-2 py-2.5 bg-gray-100 border border-gray-200 outline-none max-w-[140px]"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              disabled={missingAuth0Params}
            >
              {countryOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.countryName} ({opt.callingCode})
                </option>
              ))}
            </select>
            <input
              type="tel"
              className={`${inputClass} mt-0 flex-1`}
              placeholder={phonePlaceholder}
              value={phone}
              onChange={(e) => {
                try {
                  setPhone(new AsYouType(country).input(e.target.value));
                } catch {
                  setPhone(e.target.value);
                }
              }}
              disabled={missingAuth0Params}
              autoComplete="tel"
            />
          </div>
        </div>

        <div className="w-full lg:mt-5 mt-4">
          <p className={labelClass}>Company Name</p>
          <input
            type="text"
            className={inputClass}
            placeholder="Company Name"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            disabled={missingAuth0Params}
            autoComplete="organization"
          />
        </div>

        {isSocialLogin ? (
          <div className="w-full lg:mt-5 mt-4 flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-3 py-3 text-sm text-green-800">
            <svg
              className="h-5 w-5 shrink-0 text-green-600"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                clipRule="evenodd"
              />
            </svg>
            <span>Email verified through your sign-in provider</span>
          </div>
        ) : (
          <div className="w-full lg:mt-5 mt-4 pt-4 border-t border-gray-200">
            <p className={labelClass}>Email verification</p>
            <p className="text-xs text-gray-500 mt-1 mb-3">
              {otpVerified
                ? "Your email is verified for this session."
                : "Fill in phone and company name, then request a 6-digit code."}
            </p>

            {otpFeedback ? (
              <p
                className={`text-sm mb-3 ${
                  otpFeedback.type === "error"
                    ? "text-red-600"
                    : otpFeedback.type === "success"
                      ? "text-green-700"
                      : "text-gray-600"
                }`}
              >
                {otpFeedback.message}
              </p>
            ) : null}

            {otpVerified ? (
              <div className="flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-3 py-3 text-sm text-green-800">
                <svg
                  className="h-5 w-5 shrink-0 text-green-600"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Email verified</span>
              </div>
            ) : !otpSent ? (
              <button
                type="button"
                disabled={!canSendOtp}
                onClick={handleSendOtp}
                className="text-white bg-[#007BFF] rounded-xl text-sm w-full py-2.5 disabled:opacity-50"
              >
                {sendingOtp ? "Sending…" : "Send verification code"}
              </button>
            ) : (
              <>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  className={inputClass}
                  placeholder="Enter 6-digit code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  disabled={missingAuth0Params}
                  autoComplete="one-time-code"
                />
                <div className="flex gap-2 mt-3">
                  <button
                    type="button"
                    disabled={!canVerifyOtp}
                    onClick={handleVerifyOtp}
                    className="flex-1 text-white bg-[#007BFF] rounded-xl text-sm py-2.5 disabled:opacity-50"
                  >
                    {verifyingOtp ? "Verifying…" : "Verify code"}
                  </button>
                  <button
                    type="button"
                    disabled={sendingOtp || otpVerified || !canSendOtp}
                    onClick={handleSendOtp}
                    className="flex-1 border border-gray-200 rounded-xl text-sm py-2.5 text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Resend code
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={!canContinue}
          className="text-white bg-[#007BFF] lg:mt-6 mt-4 rounded-xl text-sm w-full lg:py-3 py-2.5 disabled:opacity-50 outline-none"
        >
          {loading ? "Saving…" : "Continue"}
        </button>
      </form>
    </AuthPageShell>
  );
}
